import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertRailBody, Rail, UpdateRailBody } from "./types/rails";

export const useRailsApi = () => {
  const api = useApi();

  const getAdminRail = useCallback(
    (railId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/rails/${railId}`;
      return useSWRMutation<Rail, CustomError, string>(url, (url) => api.get<Rail>({ url, signal }));
    },
    [api],
  );

  const useAdminRails = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rails`, query);
      return useSWRImmutable<Paginated<Rail>, CustomError, string>(url, (url) =>
        api.get<Paginated<Rail>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminRails = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rails`, query);
      return api.get<Paginated<Rail>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminRail = useCallback(
    async (body: InsertRailBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/rails`;
      const id = await api.post<{ id: string }, InsertRailBody>(url, body);
      mutateCache([url, "/admin/settings/border/rail_caps", "/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rail_corners"]);
      return id;
    },
    [api],
  );

  const updateAdminRail = useCallback(
    async (railId: string, body: UpdateRailBody): Promise<void> => {
      const url = `/admin/settings/border/rails/${railId}`;
      await api.put<UpdateRailBody>(url, body);
      mutateCache(["/admin/settings/border/rails", "/admin/settings/border/rail_caps", "/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rail_corners"]);
    },
    [api],
  );

  const updateAdminRailVisibility = useCallback(
    async (railId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rails/${railId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/rails", "/admin/settings/border/rail_caps", "/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rail_corners"]));
    },
    [api],
  );

  const updateAdminRailOrder = useCallback(
    async (railId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rails/${railId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/rails", "/admin/settings/border/rail_caps", "/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rail_corners"]));
    },
    [api],
  );

  const deleteAdminRail = useCallback(
    async (rail: Rail): Promise<void> => {
      const url = `/admin/settings/border/rails/${rail.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/rails`, "/admin/settings/border/rail_caps", "/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rail_corners"]);
    },
    [api],
  );

  return {
    getAdminRail,
    insertAdminRail,
    updateAdminRail,
    updateAdminRailVisibility,
    updateAdminRailOrder,
    deleteAdminRail,
    useAdminRails,
    getAdminRails,
  };
};
